<template>
  <div>
    <label v-html="label"></label>
    <v-row no-gutters class="align-center">
      <v-col>
        <v-text-field
          v-bind="commonProps"
          id="time-hour"
          v-model="$v.hour.$model"
          :autofocus="autofocus && index === 0"
          :placeholder="placeholders.hour"
          data-part="hour"
          :class="{
            'is-valid':
              $v.$anyDirty && !$v.hour.$invalid && $v.hour.$model.length && !timeError.length
          }"
          :error="!!timeError.length"
          :error-messages="timeError"
          v-on="commonEvents"
        />
      </v-col>
      <v-col class="text-center px-3 flex-shrink-1 flex-grow-0 text-h1">:</v-col>
      <v-col>
        <v-text-field
          v-bind="commonProps"
          id="time-minute"
          v-model="$v.minute.$model"
          :autofocus="autofocus && index === 0"
          :placeholder="placeholders.minute"
          data-part="minute"
          :class="{
            'is-valid':
              $v.$anyDirty && !$v.minute.$invalid && $v.minute.$model.length && !timeError.length
          }"
          :error="!!timeError.length"
          :error-messages="timeError"
          v-on="commonEvents"
        />
      </v-col>
    </v-row>
    <div
      v-if="(!!helpText && !!helpText.length) || !!timeError.length"
      class="v-text-field__details"
    >
      <div
        :class="{ 'error--text': !!timeError.length }"
        class="v-messages theme--light"
        role="alert"
      >
        <div class="v-messages__wrapper">
          <div
            :inner-html.prop="!!timeError.length ? timeError : helpText.length ? helpText : ''"
            class="v-messages__message text-body-small"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { between, numeric, required } from 'vuelidate/lib/validators';

export default {
  name: 'TimeSegments',
  props: {
    identifier: {
      type: [String, Number],
      required: false
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    value: {
      type: String,
      required: true
    },
    helpText: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    errorText: {
      type: String,
      required: false
    },
    errorTextRequired: {
      type: String,
      required: false
    },
    label: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      commonProps: {
        disabled: this.disabled,
        flat: true,
        hideDetails: true,
        solo: true,
        autocomplete: 'off',
        type: 'number',
        pattern: '\\d*',
        hideSpinButtons: true
      },
      commonEvents: {
        keyup: this.onKeyUp,
        blur: this.onBlur,
        focus: this.onFocus
      },
      hour: '',
      minute: '',
      placeholders: {
        hour: '',
        minute: ''
      },
      firstEmptyBackspace: false
    };
  },
  validations() {
    return {
      hour: {
        between: between(0, 23),
        numeric,
        required: this.required ? required : undefined
      },
      minute: {
        between: between(0, 59),
        numeric,
        required: this.required ? required : undefined
      }
    };
  },
  computed: {
    timeError() {
      if (!this.$v.$anyDirty) {
        return '';
      }

      if (this.errorText) {
        return this.errorText;
      }

      if (this.required && (this.$v.hour.required === false || this.$v.minute.required === false)) {
        return this.errorTextRequired || this.$t('timeInput.errors.required');
      }

      if (!this.$v.hour.between || !this.$v.minute.between) {
        return this.$t('timeInput.errors.invalidFormat');
      }

      return '';
    }
  },
  created() {
    this.$eventHub.$on(
      this.identifier ? `validateTimeSegments_${this.identifier}` : 'validateTimeSegments',
      done => {
        this.$v.$touch();
        this.$nextTick(() => {
          done(!this.$v.$invalid);
        });
      }
    );
    this.$eventHub.$on(
      this.identifier
        ? `resetTimeSegmentsValidation_${this.identifier}`
        : 'resetTimeSegmentsValidation',
      () => {
        this.$v.$reset();
      }
    );

    this.placeholders.hour = this.$t('placeholders.time.hour');
    this.placeholders.minute = this.$t('placeholders.time.minute');

    if (this.value) {
      const [initialHour, initialMinute] = this.value.split(':');
      this.hour = initialHour;
      this.minute = initialMinute;
    }
  },
  beforeDestroy() {
    this.$eventHub.$off(
      this.identifier ? `validateTimeSegments_${this.identifier}` : 'validateTimeSegments'
    );
    this.$eventHub.$off(
      this.identifier
        ? `resetTimeSegmentsValidation_${this.identifier}`
        : 'resetTimeSegmentsValidation'
    );
  },
  methods: {
    preventScroll(evt) {
      evt.preventDefault();
    },
    onKeyUp(evt) {
      if (evt.keyCode === this.keyCodes.shift) {
        return;
      }

      evt.preventDefault();
      this.$v.$touch();

      const part = evt.target.dataset.part;
      let prevPart = part === 'hour' ? null : 'minute';
      let nextPart = part === 'hour' ? 'minute' : null;

      switch (evt.keyCode) {
        case this.keyCodes.backspace:
          // eslint-disable-next-line no-case-declarations
          const isEmpty =
            `${this[part]}`.length === 0 && this.$el.querySelector(`#time-${prevPart}`);
          if (isEmpty && !this.firstEmptyBackspace) {
            this.firstEmptyBackspace = true;
            return;
          } else if (isEmpty && this.firstEmptyBackspace) {
            const prevInput = this.$el.querySelector(`#time-${prevPart}`);
            prevInput.focus();
            prevInput.select();
          }
          break;
        case this.keyCodes.up:
        case this.keyCodes.down:
          break;
        default:
          if (evt.keyCode !== 229 && isNaN(parseInt(evt.key))) {
            return;
          } else {
            if (`${this[part]}`.length === 2 && this.$el.querySelector(`#time-${nextPart}`)) {
              const nextInput = this.$el.querySelector(`#time-${nextPart}`);
              nextInput.focus();
              nextInput.select();
            } else if (part === 'minute' && `${this.minute}`.length === 2) {
              this.$el.querySelector('#time-minute').blur();
              this.$emit('blur');
            }
          }
          break;
      }

      this.firstEmptyBackspace = false;

      this.callInput();
    },
    onBlur(evt) {
      const partBlurred = evt.target.dataset.part;
      this[partBlurred] = !this[partBlurred]
        ? this[partBlurred]
        : this.padStartWithZeroes(this[partBlurred], 2);
      this.$emit('blurPart', partBlurred);
      evt.target.removeEventListener('wheel', this.preventScroll);
    },
    onFocus(evt) {
      evt.target.addEventListener('wheel', this.preventScroll);
      this.$emit('focus', evt);
    },
    callInput() {
      const hourToSend = this.hour ? this.padStartWithZeroes(this.hour, 2) : '00';
      const minuteToSend = this.minute ? this.padStartWithZeroes(this.minute, 2) : '00';

      const valueToSend =
        parseInt(hourToSend) >= 0 && parseInt(minuteToSend) >= 0
          ? `${hourToSend}:${minuteToSend}`
          : '';
      this.$emit('input', valueToSend);
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep {
  input[type='number'] {
    text-align: center;
  }
}
</style>
